"use client";

import {
  Alert,
  App,
  Button,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Tooltip,
  Typography,
} from "antd";
import { useEffect } from "react";
import { useCatalog } from "@/catalog/CatalogContext";
import {
  deleteSegmentBlockReason,
  ipListName,
  segmentStatus,
  statusExplanation,
  type Segment,
  type SegmentInput,
  type Vendor,
} from "@/data/catalog";
import { StatusTag } from "./StatusTag";
import styles from "./catalog.module.css";

interface SegmentDrawerProps {
  open: boolean;
  mode: "view" | "edit" | "create";
  vendor: Vendor;
  segment?: Segment;
  onClose: () => void;
  onMode: (mode: "view" | "edit") => void;
}

export function SegmentDrawer({
  open,
  mode,
  vendor,
  segment,
  onClose,
  onMode,
}: SegmentDrawerProps) {
  const { createSegment, updateSegment, deleteSegment } = useCatalog();
  const { message } = App.useApp();
  const [form] = Form.useForm<SegmentInput>();
  const pending = segment != null && segment.akDspIpListId == null;
  const blockReason = segment ? deleteSegmentBlockReason(segment) : null;

  useEffect(() => {
    if (!open || mode === "view") return;
    form.setFieldsValue({
      name: segment?.name ?? "",
      description: segment?.description ?? "",
      segmentId: segment?.segmentId ?? null,
      active: segment?.storedActive ?? false,
    });
  }, [form, mode, open, segment]);

  const save = async () => {
    const values = await form.validateFields();
    const input: SegmentInput = {
      name: values.name,
      description: values.description ?? "",
      segmentId: values.segmentId,
      active: values.active,
    };
    const result =
      mode === "create" || segment == null
        ? createSegment(vendor.id, input)
        : updateSegment(segment.id, input);
    if (!result.ok) {
      form.setFields([{ name: result.field, errors: [result.message] }]);
      return;
    }
    message.success(mode === "create" ? "Segment created" : "Segment updated");
    if (mode === "create") {
      onClose();
      return;
    }
    onMode("view");
  };

  const remove = () => {
    if (!segment) return;
    const result = deleteSegment(segment.id);
    if (!result.ok) {
      message.error(result.message);
      return;
    }
    message.success("Segment deleted");
    onClose();
  };

  const title =
    mode === "create" ? "Add segment" : mode === "edit" ? "Edit segment" : segment?.name;

  const footer =
    mode === "view" && segment ? (
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        {blockReason ? (
          <Tooltip title={blockReason}>
            <span>
              <Button danger disabled>
                Delete
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Popconfirm
            title="Delete this segment?"
            description="Pending and inactive segments can be deleted when no campaign uses them."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={remove}
          >
            <Button danger>Delete</Button>
          </Popconfirm>
        )}
        <Button type="primary" onClick={() => onMode("edit")}>
          Edit
        </Button>
      </div>
    ) : (
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button
          onClick={() => {
            if (mode === "edit" && segment) onMode("view");
            else onClose();
          }}
        >
          Cancel
        </Button>
        <Button type="primary" onClick={() => void save()}>
          {mode === "create" ? "Create segment" : "Save"}
        </Button>
      </div>
    );

  return (
    <Drawer title={title} open={open} onClose={onClose} destroyOnHidden size={520} footer={footer}>
      {mode === "view" && segment ? (
        <SegmentFacts segment={segment} />
      ) : (
        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, whitespace: true, message: "Enter a segment name." }]}
          >
            <Input maxLength={80} showCount placeholder="Quit-smoking intenders" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea maxLength={200} showCount rows={3} placeholder="Who this audience is" />
          </Form.Item>
          <Form.Item
            label="Vendor segment ID"
            name="segmentId"
            extra="The id the vendor assigned when the audience was built. It is not checked against the feed."
            rules={[{ required: true, message: "Enter the vendor segment id." }]}
          >
            <InputNumber min={1} precision={0} style={{ width: "100%" }} placeholder="1106480" />
          </Form.Item>
          {mode === "create" || pending ? (
            <Alert
              type="info"
              showIcon
              message="New segments start as Pending."
              description="They become Active after an AdKernel IP list is bound. Until then they are approved for ingestion and not targetable."
            />
          ) : (
            <Form.Item
              label="Status"
              name="active"
              required
              extra="Inactive keeps the IP list, hides the segment from pickers, and stops the daily refresh."
            >
              <Radio.Group>
                <Radio value={true}>Active</Radio>
                <Radio value={false}>Inactive</Radio>
              </Radio.Group>
            </Form.Item>
          )}
          {segment && segment.targetingReferences > 0 && (
            <Alert
              style={{ marginTop: 16 }}
              type="warning"
              showIcon
              message={`On ${segment.targetingReferences} campaign${segment.targetingReferences === 1 ? "" : "s"} or line item${segment.targetingReferences === 1 ? "" : "s"}.`}
              description={`They store vendor segment id ${segment.segmentId}. Changing the id does not update those campaigns. Delete stays blocked until they drop it.`}
            />
          )}
        </Form>
      )}
    </Drawer>
  );
}

function SegmentFacts({ segment }: { segment: Segment }) {
  const status = segmentStatus(segment);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <StatusTag segment={segment} />
      </div>
      <Typography.Paragraph style={{ margin: 0 }}>{statusExplanation(segment)}</Typography.Paragraph>
      {segment.description ? (
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          {segment.description}
        </Typography.Paragraph>
      ) : null}
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Vendor segment ID">
          <span className={styles.mono}>{segment.segmentId}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Catalog id">
          <span className={styles.mono}>{segment.id}</span>
        </Descriptions.Item>
        <Descriptions.Item label="AdKernel IP list">
          {segment.akDspIpListId == null ? (
            "Not bound"
          ) : (
            <span className={styles.mono}>{segment.akDspIpListId}</span>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="List name">
          {status === "PENDING" ? (
            "Assigned when the list is created"
          ) : (
            <Typography.Text className={styles.mono} copyable>
              {ipListName(segment)}
            </Typography.Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Used by targeting">
          {segment.targetingReferences === 0
            ? "No campaigns or line items"
            : `${segment.targetingReferences} campaign${segment.targetingReferences === 1 ? "" : "s"} or line item${segment.targetingReferences === 1 ? "" : "s"}`}
        </Descriptions.Item>
      </Descriptions>
      <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
        The list name is how ingestion finds the AdKernel list: the vendor segment id, an
        underscore, then a slug of the segment name.
      </Typography.Paragraph>
    </div>
  );
}
